import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon, ShieldCheckIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <section className="py-16 bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center text-primary-400 hover:text-primary-300 mb-8 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-400">
            Please read these terms carefully before registering with Oldham Athletics.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
            
            {/* Annual Fees */}
            <div className="mb-12">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mr-4">
                  <CreditCardIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Annual Fees and Payment Instructions</h2>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-600">
                <div className="space-y-4 text-gray-300">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p><strong className="text-white">Annual Fees:</strong> $5 (students, NSFs, retirees), $10 (working adults)</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>This registration fee is only applied for new sign-ups as the committee has decided to waive the annual registration fee for 2020 and 2021.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>Payment will be made directly to Mr Kevin Ng (Treasurer) via Paylah or PayNow.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Waiver and Release */}
            <div className="mb-12">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                  <ShieldCheckIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Waiver and Release from Liability</h2>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-600">
                <div className="space-y-4 text-gray-300">
                  <p>By signing up you agree that you participate in all Oldham Athletics activities at your own risk and are responsible for your own health, safety and belongings and that Oldham Athletics will not be held liable for any loss of or damage to your property, accidents, injuries and death arising from participation in any activities organized by Oldham Athletics.</p>
                  
                  <p>The risks that you are exposed to include actions of other people (including volunteers, spectators, coaches, event officials, event monitors, participants and agents), lack of hydration, weather and/or other natural conditions.</p>
                  
                  <p>You further agree to indemnify Oldham Athletics, volunteers, coaches, event officials, event monitors, participants and agents against any claims, causes of action, liabilities, fines, fees, cost and expenses incurred or sustained by Oldham Athletics and/or the aforementioned parties.</p>
                  
                  <p>In the event of an accident or illness requiring urgent medical treatment, you give permission to receive any emergency dental, medical or surgical treatment, including blood transfusion, as considered necessary by the medical authorities present.</p>
                  
                  <p>Please consult your doctor or medical professional on your suitability to participate in any of these events, as you deem fit. It will be beneficial and it is recommended that you have your own personal accident insurance coverage.</p>
                </div>
              </div>
            </div>

            {/* Personal Data */}
            <div className="mb-12">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <DocumentTextIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white">Personal Data</h2>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-600">
                <div className="space-y-4 text-gray-300">
                  <p>Oldham Athletics will need to collect, use, disclose and/or process your personal data for the purpose of verifying your identity, granting you access to and use of any relevant venue, and to process your application or requests for participation in the activities.</p>
                  
                  <p>By providing your personal data in this form, you consent to the collection, use and/or disclosure of your personal data by Oldham Athletics for the aforementioned purposes. You further consent to receive and be contacted via email, SMS, WhatsApp, and voice call on upcoming events from the authorized organizing committee.</p>
                  
                  <p>You further give consent to Oldham Athletics to use your images from any photo/film shoots and waive all rights to the images. This includes reproductions or adaptations of the images for general purposes (e.g. website and articles).</p>
                  
                  <p>Your privacy is our priority and your personal data provided will not be shared with any unrelated parties except as contemplated above. Thank you.</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-primary-500/20 to-blue-500/20 rounded-xl p-6 border border-primary-500/30">
              <h3 className="text-xl font-semibold text-white mb-4">Questions About These Terms?</h3>
              <p className="text-gray-300 mb-4">
                If you have any questions about these terms and conditions, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:acs.oldham.athletics@gmail.com"
                  className="bg-white text-gray-900 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
                >
                  Email Us
                </a>
                <Link
                  to="/register"
                  className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
                >
                  Proceed to Registration
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
